import { useMemo, useRef } from 'react';

import * as T from '../../../../components/Typography';
import { BasicInput } from '../../../../components/Inputs';
import * as S from './style';
import { Icon } from '../../../../components';
import { MediaKeyInput } from '../../../components/MediaKeyInput';
import MDEditor from '../../../../components/MDEditor';

import { string, number, boolean, array, object } from 'yup';
import { mediaTypes } from '../../../../constants';
import { useStepForm } from 'CMS/Providers/StepFormProvider';

const initialData = {
  options: [{ id: 0, option: '' }],
  questionText: '',
  textAudioKey: '',
  textAudio: '',
};

const SentenceFill = () => {
  // Use useRef to keep the initial state constant across renders
  const initialRef = useRef(initialData);
  const { state, updateFormFields, updateDataFields } = useStepForm(
    initialRef.current
  );
  const { form, data, validationErrs } = state;

  const options = useMemo(
    () =>
      data?.options?.map((e, i) => ({ ...e, id: i })) || [
        { id: 0, option: '' },
      ],
    [data?.options]
  );

  const addOption = () => {
    const maxId = Math.max(...options.map((e) => e.id), 0);
    updateDataFields({
      options: [...options, { id: maxId + 1, option: '' }],
    });
  };
  const removeOption = (id) => {
    updateDataFields({
      options: options.filter((e) => e.id !== id),
    });
  };

  const onOptionChange = (value, id) => {
    updateDataFields({
      options: options.map((e) => {
        if (e.id !== id) return e;
        return { ...e, option: value };
      }),
    });
  };
  const onAudioKeyChange = (value, id) => {
    updateDataFields({
      options: options.map((e) => {
        if (e.id !== id) return e;
        return { ...e, audioKey: value };
      }),
    });
  };

  return (
    <div>
      <BasicInput
        label="Explainer Text"
        value={data.textAudio}
        handleChange={(textAudio) => updateDataFields({ textAudio })}
        m={{ mt: 8 }}
      />
      <MediaKeyInput
        label={`Explainer Audio file Key`}
        type={mediaTypes.TEXT_AUDIO}
        value={data.textAudioKey}
        handleChange={(textAudioKey) => updateDataFields({ textAudioKey })}
        m={{ mt: 5 }}
      />
      <MDEditor
        label="Full Sentence"
        value={data.questionText}
        onChange={(questionText) => updateDataFields({ questionText })}
        m={{ mt: 8 }}
        helper="Put % around the word you want to hide"
        error={validationErrs?.questionText}
        height={100}
      />
      <MediaKeyInput
        label={`Sentence audio file Key`}
        type={mediaTypes.AUDIO}
        value={form.audioKey}
        handleChange={(audioKey) => updateFormFields({ audioKey })}
        m={{ mt: 5 }}
        error={validationErrs?.audioKey} // TODO: investigate why error is happening
      />

      <T.P mt={8} size="large" weight="bold">
        Options
      </T.P>
      {validationErrs?.options &&
        typeof validationErrs.options === 'string' && (
          <T.P mb={2} mt={2} color="error">
            {validationErrs.options}
          </T.P>
        )}

      {options.map((e, i) => {
        return (
          <div style={{ marginBottom: '25px' }}>
            <BasicInput
              label={`Option ${i + 1}`}
              value={e.option}
              handleChange={(value) => {
                onOptionChange(value, e.id);
              }}
              m={{ mt: 5 }}
              error={validationErrs?.options?.[i]?.option}
            />

            <MediaKeyInput
              index={i}
              type={mediaTypes.OPTION_AUDIO}
              label={`Option ${i + 1} Audio file Key`}
              value={e.audioKey}
              handleChange={(value) => {
                onAudioKeyChange(value, e.id);
              }}
              m={{ mt: 5 }}
              error={validationErrs?.options?.[i]?.audioKey}
            />

            {options.length === 1 && i === 0 ? null : (
              <S.Button
                type="link"
                danger
                ghost
                mt={1}
                onClick={() => removeOption(e.id)}
                ml="auto"
              >
                <Icon icon="cross" />
              </S.Button>
            )}
          </div>
        );
      })}
      <S.Button
        type="link"
        ghost
        mt={5}
        mb={5}
        onClick={addOption}
        disabled={options.find((e) => !e.option)}
        ml="auto"
        mr="auto"
      >
        <Icon icon="plus" /> Add Option
      </S.Button>
    </div>
  );
};

const validationSchema = {
  options: array()
    .of(
      object()
        .shape({
          id: number().required(),
          option: string().required('required field'),
          audioKey: string().required('required field'),
          isCorrect: boolean(),
        })
        .required()
    )
    .required('you must provide options')
    .min(1, 'you must provide options'),
  questionText: string()
    .required('required field')
    .test(
      'has-valid-hidden-words',
      'Hidden words must be included and properly formatted with % on both sides (e.g., %word%)',
      function (value) {
        if (!value) return false;
        if (!value.includes('%')) return false;
        if (value.includes('%%')) return false;

        const hiddenWordPattern = /%[a-zA-Z0-9]+%/g;
        const validMatches = value.match(hiddenWordPattern) || [];

        const totalPercents = (value.match(/%/g) || []).length;

        const expectedPairs = totalPercents / 2;
        return totalPercents % 2 === 0 && validMatches.length === expectedPairs;
      }
    ),
  textAudioKey: string().notRequired(),
  textAudio: string().notRequired(),
  audioKey: string().required('required field'),
};
export { validationSchema };

export default SentenceFill;
